/**
 * homeView — everything the Home page shows, derived from the deals and the
 * activity log that HomeDashboard already loads. Pure functions: no fetching,
 * no React, so every figure on the page can be checked against the database
 * by calling these directly.
 *
 * Home is also the page we put in front of partners and investors, so two
 * rules run through all of it:
 *
 *   1. A figure that cannot be backed is left out, not estimated. A deal with
 *      no logged move into its current stage has an UNKNOWN age, and is not
 *      flagged for one — flagging it is how Texas Star, the deal closest to
 *      closing, came to be listed as neglected.
 *   2. Each deal counts once. A deal tagged to two markets goes under the first,
 *      so the market shares sum to 100% and a portfolio cannot double the
 *      apparent weight of a market.
 *
 * "Active" means Underwriting and later (ACTIVE_STAGES), and book value is our
 * underwritten price where we have one, else the ask (dealValue) — both shared
 * with homeBrief so the two cannot drift.
 */

import { ACTIVE_STAGES, dealValue } from "./homeBrief";
import { HOME_MAP_REGIONS } from "../constants/homeRegions";
import { parseDealName } from "../utils/parseDealName";

/** Closest to close first — the order every section on Home reads in. */
export const CLOSE_ORDER = ["In Diligence", "LOI Submitted", "Underwriting"];

export const STAGE_LABEL = {
  "In Diligence": "In diligence",
  "LOI Submitted": "LOI",
  "Underwriting": "Underwriting",
};

/** Equity share assumed against book value. Matches the 65% LTV fund cap with room to spare. */
export const DEFAULT_EQUITY_PCT = 0.4;

const DAY_MS = 86_400_000;
const DEAD = ["Passed", "Lost", "Dead"];

// Number(null) and Number("") are both 0, which turns every empty field into a
// real zero; this keeps them empty.
const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

const median = (xs) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

export const dealTitle = (d) => parseDealName(d.sourceNotes) || d.addresses?.[0] || "Untitled deal";

/* ── the book ─────────────────────────────────────────────────────── */
/**
 * Every active deal as a row, closest to close first, with its book value,
 * area and returns resolved, plus the totals and the split by stage.
 *
 * $/SF divides by the area of PRICED deals only. A deal carrying square footage
 * but no price would otherwise add area to the denominator and nothing to the
 * numerator, and understate the basis for the whole book.
 */
export function activeBook(deals = []) {
  const rows = deals
    .filter((d) => ACTIVE_STAGES.includes(d.stage))
    .map((d) => {
      const value = dealValue(d);
      const sf = num(d.squareFeet) || 0;
      const irr = num(d.leveredIrr);
      const em = num(d.equityMultiple);
      return {
        deal: d,
        id: d.id,
        name: dealTitle(d),
        stage: d.stage,
        market: (d.msas && d.msas[0]) || "Unassigned",
        value,
        sf,
        psf: value > 0 && sf > 0 ? value / sf : null,
        irr: irr != null && irr > 0 ? irr : null,           // stored as percent: 15.8 means 15.8%
        em: em != null && em > 0 ? em : null,
        offMarket: !d.marketed,
        lat: num(d.latitude),
        lng: num(d.longitude),
      };
    })
    .sort((a, b) => CLOSE_ORDER.indexOf(a.stage) - CLOSE_ORDER.indexOf(b.stage) || b.value - a.value);

  const total = rows.reduce((s, r) => s + r.value, 0);
  const totalSf = rows.reduce((s, r) => s + r.sf, 0);
  const pricedSf = rows.filter((r) => r.value > 0).reduce((s, r) => s + r.sf, 0);

  const byStage = CLOSE_ORDER.map((stage) => {
    const g = rows.filter((r) => r.stage === stage);
    return { stage, n: g.length, value: g.reduce((s, r) => s + r.value, 0) };
  });

  return {
    rows,
    total,
    totalSf,
    psf: pricedSf > 0 ? total / pricedSf : null,
    offMarket: rows.filter((r) => r.offMarket).length,
    unpriced: rows.filter((r) => !(r.value > 0)).length,
    byStage,
  };
}

/* ── equity, closest to close first ───────────────────────────────── */
/** Equity each stage would need, and the running total from diligence outward. */
export function equityLadder(book, equityPct = DEFAULT_EQUITY_PCT) {
  let cum = 0;
  return book.byStage.map((b) => {
    const equity = b.value * equityPct;
    cum += equity;
    return { ...b, equity, cumulative: cum };
  });
}

/* ── markets, each deal once ──────────────────────────────────────── */
export function marketsOnce(book) {
  const by = new Map();
  for (const r of book.rows) {
    const m = by.get(r.market) || { market: r.market, value: 0, n: 0 };
    m.value += r.value;
    m.n += 1;
    by.set(r.market, m);
  }
  return [...by.values()]
    .map((m) => ({ ...m, share: book.total > 0 ? m.value / book.total : null }))
    .sort((a, b) => b.value - a.value);
}

/* ── map regions ──────────────────────────────────────────────────── */
/** Active deals grouped into map panels by coordinates, largest region first. */
export function mapRegions(book) {
  const within = (r, b) => r.lat >= b.latMin && r.lat <= b.latMax && r.lng >= b.lngMin && r.lng <= b.lngMax;
  const groups = new Map();
  for (const r of book.rows) {
    if (r.lat == null || r.lng == null) continue;
    const region = HOME_MAP_REGIONS.find((g) => within(r, g.bounds))?.name || "Other";
    if (!groups.has(region)) groups.set(region, []);
    groups.get(region).push(r);
  }
  return [...groups.entries()]
    .map(([name, rows]) => ({ name, rows, value: rows.reduce((s, r) => s + r.value, 0) }))
    .sort((a, b) => b.value - a.value);
}

/* ── returns ──────────────────────────────────────────────────────── */
export function returnsSummary(book) {
  const underwritten = book.rows.filter((r) => r.irr != null).sort((a, b) => a.irr - b.irr);
  const irrs = underwritten.map((r) => r.irr);
  return {
    underwritten,
    n: underwritten.length,
    of: book.rows.length,
    medianIrr: median(irrs),
    minIrr: irrs.length ? irrs[0] : null,
    maxIrr: irrs.length ? irrs[irrs.length - 1] : null,
    medianEm: median(book.rows.map((r) => r.em).filter((v) => v != null)),
  };
}

/* ── how long each deal has sat in its stage ──────────────────────── */
/**
 * Days since the deal's last LOGGED move into the stage it is in now, or null.
 *
 * Only a move INTO the current stage counts. The log misses bulk loads, so the
 * latest event on a deal is often a move into some earlier stage; measuring
 * from that reports the time since the deal last moved anywhere, which for a
 * deal loaded straight into Underwriting last week can read as a hundred days.
 */
export function daysInStage(deals = [], stageLog = [], now = new Date()) {
  const current = new Map(deals.map((d) => [d.id, d.stage]));
  const into = new Map();
  for (const e of stageLog) {
    if (e.action && e.action !== "stage_changed") continue;
    if (!e.entity_id || e.metadata?.new_stage !== current.get(e.entity_id)) continue;
    const t = Date.parse(e.created_at);
    if (Number.isFinite(t) && (!into.has(e.entity_id) || t > into.get(e.entity_id))) into.set(e.entity_id, t);
  }
  const out = {};
  for (const [id, t] of into) out[id] = Math.floor((now.getTime() - t) / DAY_MS);
  return out;
}

/* ── watchlist ────────────────────────────────────────────────────── */
/** Days past which a deal in each stage is worth a look. Diligence is absent on purpose. */
export const WATCH_RULES = { "Underwriting": 60, "LOI Submitted": 45 };

/**
 * Deals worth a look, by rules that depend on the stage.
 *
 * Diligence is never flagged for sitting still — a deal in DD sits in one stage
 * for weeks while the real work happens, and is tracked against its dates. A
 * passed call for offers flags regardless of age, because the date is recorded
 * on the deal and does not depend on the log.
 */
export function watchlist(book, ages = {}, now = new Date()) {
  const items = [];
  let unaged = 0;
  for (const r of book.rows) {
    const d = r.deal;
    const age = ages[r.id];
    const cfo = d.callForOffersDate ? new Date(d.callForOffersDate) : null;
    if (r.stage === "Underwriting" && cfo && Number.isFinite(cfo.getTime()) && cfo < now) {
      const past = Math.max(1, Math.round((now - cfo) / DAY_MS));
      items.push({ row: r, why: `Call for offers passed ${cfo.getUTCMonth() + 1}/${cfo.getUTCDate()} and we're still underwriting`, age: `${past}d past` });
      continue;
    }
    if (!(r.stage in WATCH_RULES)) continue;
    if (age == null) { unaged += 1; continue; }
    if (age > WATCH_RULES[r.stage]) {
      items.push({
        row: r,
        why: r.stage === "Underwriting" ? `In underwriting past ${WATCH_RULES[r.stage]} days` : `LOI out past ${WATCH_RULES[r.stage]} days with no answer`,
        age: `${age}d`,
      });
    }
  }
  return { items, unaged };
}

/* ── this week ────────────────────────────────────────────────────── */
/** Movement in the activity rows HomeDashboard already scopes to the current week. */
export function weekMoves(activities = []) {
  const moves = activities.filter((a) => a.action === "stage_changed");
  const into = (s) => moves.filter((a) => a.metadata?.new_stage === s).length;
  return {
    toUnderwriting: into("Underwriting"),
    toLoi: into("LOI Submitted"),
    toDiligence: into("In Diligence"),
    closedOut: moves.filter((a) => DEAD.includes(a.metadata?.new_stage)).length,
    newDeals: activities.filter((a) => a.action === "deal_created").length,
    total: moves.length,
  };
}

/* ── the paste-into-an-email line ─────────────────────────────────── */
export function partnerSummary(book, returns) {
  const m = (v) => `$${(v / 1e6).toFixed(1)}M`;
  const [dd, loi, uw] = book.byStage;
  const close = book.rows.filter((r) => r.stage !== "Underwriting").map((r) => r.name);
  const parts = [
    `${m(book.total)} under review across ${book.rows.length} deal${book.rows.length === 1 ? "" : "s"}`
      + ` — ${m(dd.value)} in diligence, ${m(loi.value)} at LOI, ${m(uw.value)} in underwriting.`,
  ];
  if (close.length) parts.push(`Closest to close: ${close.join(", ")}.`);
  if (returns.medianIrr != null) parts.push(`Median levered IRR ${returns.medianIrr.toFixed(1)}% across the ${returns.n} underwritten.`);
  return parts.join(" ");
}
